import React, { useState } from 'react';
import { Network, Server, Database, Activity, Search, ShieldCheck, ArrowRight, Play, Pause } from 'lucide-react';

interface ServiceNode {
  id: string;
  name: string;
  type: string;
  rps: number;
  p95Latency: number;
  errorRate: number;
  activePool: number;
  status: 'healthy' | 'warning' | 'degraded';
  targetDb: string;
}

interface APMTrace {
  traceId: string;
  serviceName: string;
  endpoint: string;
  query: string;
  durationMs: number;
  timestamp: string;
  status: '200 OK' | '500 ERROR' | 'SLOW';
}

interface CallingServicesViewProps {
  serviceNodes?: ServiceNode[];
  apmTraces?: APMTrace[];
}

export const CallingServicesView: React.FC<CallingServicesViewProps> = ({
  serviceNodes = [],
  apmTraces = [],
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isLiveTracing, setIsLiveTracing] = useState(true);
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const filteredServices = serviceNodes.filter((s) =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Top Banner / Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gradient-to-r from-blue-900/30 via-indigo-900/20 to-purple-900/30 border border-blue-500/30 rounded-xl p-4 shadow-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-blue-400">
            <Network className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              Calling Services & Microservice APM Topology
              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                APM ACTIVE
              </span>
            </h2>
            <p className="text-xs text-gray-300">
              Live service dependency graph and APM tracing map connecting external microservices to PostgreSQL.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsLiveTracing(!isLiveTracing)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              isLiveTracing
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-gray-800 text-gray-400 border border-gray-700'
            }`}
          >
            {isLiveTracing ? <Play className="w-3.5 h-3.5 fill-current animate-pulse" /> : <Pause className="w-3.5 h-3.5" />}
            {isLiveTracing ? 'Live APM Tailing' : 'Tailing Paused'}
          </button>
        </div>
      </div>

      {/* APM Topology Flow Diagram Card */}
      <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-bold text-gray-200 uppercase tracking-wider flex items-center gap-2">
            <Activity className="w-4 h-4 text-purple-400" />
            Service-To-Database Flow Architecture
          </h3>
          <div className="relative w-60">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search service node..."
              className="w-full bg-gray-900/60 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-xs text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>

        {/* Nodes Flow Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          {/* Microservices Column */}
          <div className="md:col-span-7 space-y-3">
            {filteredServices.length > 0 ? (
              filteredServices.map((srv) => {
                const isSelected = selectedService === srv.name;
                return (
                  <div
                    key={srv.id}
                    onClick={() => setSelectedService(isSelected ? null : srv.name)}
                    className={`cursor-pointer p-3.5 rounded-xl border transition-all ${
                      isSelected
                        ? 'bg-blue-900/30 border-blue-500 text-white shadow-lg shadow-blue-950/50 scale-[1.01]'
                        : 'bg-gray-900/60 border-gray-700/60 hover:bg-gray-800/60 text-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400">
                          <Server className="w-4 h-4" />
                        </div>
                        <div>
                          <h4 className="text-xs font-bold text-white font-mono">{srv.name}</h4>
                          <span className="text-[10px] text-gray-400">{srv.type}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-xs font-mono">
                        <div className="text-right">
                          <span className="text-[10px] text-gray-400 block uppercase">RPS</span>
                          <span className="font-bold text-blue-400">{srv.rps}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-[10px] text-gray-400 block uppercase">p95 Latency</span>
                          <span className={`font-bold ${srv.p95Latency > 30 ? 'text-amber-400' : 'text-green-400'}`}>
                            {srv.p95Latency}ms
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-[10px] text-gray-400 block uppercase">DB Pool</span>
                          <span className="font-bold text-gray-300">{srv.activePool}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center text-xs text-gray-400 italic bg-gray-900/40 rounded-xl border border-gray-700/40">
                Calling Services extraction is currently only available for APM-enabled clusters. Connect APM agent to visualize upstream service flows.
              </div>
            )}
          </div>

          {/* Connection Lines / Arrow */}
          <div className="hidden md:flex md:col-span-1 flex-col items-center justify-center space-y-4">
            <div className="w-full h-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 animate-pulse" />
            <ArrowRight className="w-5 h-5 text-blue-400" />
            <div className="w-full h-0.5 bg-gradient-to-r from-indigo-500 to-emerald-500 animate-pulse" />
          </div>

          {/* Target DB Nodes Column */}
          <div className="md:col-span-4 space-y-3">
            <div className="bg-gradient-to-br from-emerald-950/40 to-gray-900 border border-emerald-500/40 rounded-xl p-4 shadow-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                  <Database className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-white font-mono">PostgreSQL Primary Cluster</h4>
                  <span className="text-[10px] text-emerald-400 font-semibold">MASTER (RW)</span>
                </div>
              </div>
              <div className="text-[11px] text-gray-300 font-mono space-y-1 mt-3 pt-2 border-t border-emerald-500/20">
                <div className="flex justify-between">
                  <span className="text-gray-400">Connected Services:</span>
                  <span className="font-bold text-white">{filteredServices.length} Services</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-950/40 to-gray-900 border border-blue-500/40 rounded-xl p-4 shadow-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-blue-400">
                  <Database className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-xs font-bold text-white font-mono">PostgreSQL Replica Nodes</h4>
                  <span className="text-[10px] text-blue-400 font-semibold">READ REPLICAS (RO)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* APM Trace Inspector Table */}
      <div className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4 border-b border-gray-700/50 pb-3">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            Live Distributed Traces (APM Trace Samples)
          </h3>
          <span className="text-xs text-gray-400 font-mono">{apmTraces.length} traces loaded</span>
        </div>

        <div className="overflow-x-auto custom-scroll">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="border-b border-gray-700/60 text-[11px] text-gray-400 uppercase tracking-wider">
                <th className="py-2.5 px-3 w-28">Trace ID</th>
                <th className="py-2.5 px-3 w-40">Calling Service</th>
                <th className="py-2.5 px-3 w-44">Endpoint / Job</th>
                <th className="py-2.5 px-3">Executed Query</th>
                <th className="py-2.5 px-3 text-right w-24">Latency</th>
                <th className="py-2.5 px-3 text-right w-24">Timestamp</th>
                <th className="py-2.5 px-3 text-center w-28">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30 text-gray-200">
              {apmTraces.length > 0 ? (
                apmTraces.map((tr, idx) => (
                  <tr key={idx} className="hover:bg-gray-800/50 transition-colors">
                    <td className="py-2.5 px-3 font-mono font-bold text-blue-400">{tr.traceId}</td>
                    <td className="py-2.5 px-3 font-semibold text-white font-mono">{tr.serviceName}</td>
                    <td className="py-2.5 px-3 font-mono text-gray-300">{tr.endpoint}</td>
                    <td className="py-2.5 px-3 font-mono text-blue-300 max-w-md truncate" title={tr.query}>
                      {tr.query}
                    </td>
                    <td className={`py-2.5 px-3 text-right font-mono font-bold ${tr.durationMs > 100 ? 'text-red-400' : 'text-gray-200'}`}>
                      {tr.durationMs}ms
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-gray-400">{tr.timestamp}</td>
                    <td className="py-2.5 px-3 text-center">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          tr.status === '200 OK'
                            ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                            : tr.status === 'SLOW'
                            ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                            : 'bg-red-500/10 text-red-400 border border-red-500/20'
                        }`}
                      >
                        {tr.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-400 italic">
                    No distributed trace samples captured for selected cluster.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
